import React, { useState, useEffect, useMemo } from 'react';
import { format, formatDate } from 'date-fns';
import { BASE_URL } from '@/config/api';
import {
  Calendar as CalendarIcon,
  CalendarCog,
  Clock,
  Check,
  Edit,
  MapPin,
  User,
  Users,
  FileText,
  HandCoins,
  Receipt,
  Lock,
  X,
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  ChevronDown,
  ArrowLeft,
  Info,
  GraduationCap,
  Tag,
  Building,
  InfoIcon,
  Cog,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { eventService } from '@/services/eventService';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import AddStudentForm from './AddStudentForm';
import AddLocationForm from './AddLocationForm';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import AddCategoryForm from './AddCategoryForm';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import moment from 'moment-timezone';
import { CustomTimePicker } from '../CustomTimePicker';
import { TeacherSelector } from './TeacherSelector';
import AddExtraForm from '@/components/AddExtraForm';
import { useQueryClient } from '@tanstack/react-query';


interface EventDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: any;
  students: { id: string; name?: string, firstname:string, lastname:string }[];
  categories: { id: string; name: string; color: string; price: number; s_std_pay_type?: number }[];
  teachers: {  id: string; name?: string, firstname:string, lastname:string }[];
  locations: { id: string; name: string }[];
  events: any;
  setNewLesson: any;
  teacherPrices: any[];
  handleStudentAdded: (firstname: string, lastname: string, id: any) => void;
  onMarkComplete?: (eventId: string) => void;
  onUnmarkedComplete?: (eventId: string) => void;
  onUpdate?: (eventData: any) => void;
  onRefreshEvents?: () => void;
  handleCategoryAdded: (name: string, color: string, paymentType: number, prices: Record<string, number>, newCategoryId:any) => void;
  handleLocationAdded: (name: string) => void;
  setShowNewLessonDialog?: any;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  open,
  onOpenChange,
  event,
  students,
  categories,
  events,
  teachers,
  locations,
  onMarkComplete,
  onUnmarkedComplete,
  handleStudentAdded,
  handleCategoryAdded,
  onUpdate,
  setNewLesson,
  teacherPrices,
  setShowNewLessonDialog,
  onRefreshEvents
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [eventData, setEventData] = useState<any>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isOwned, setIsOwned] = useState(false);
  const [isTeacherEvent, setIsTeacherEvent] = useState(false);
  const [invoiceType, setInvoiceType] = useState<any>(null);
  const [isAllDay, setIsAllDay] = useState(false);
  const [totalPrice, setTotalPrice] = useState<number | null>(null);
  const [duration, setDuration] = useState(60);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteAllRecurrences, setDeleteAllRecurrences] = useState(false);
  const [saveAllRecurrence, setSaveAllRecurrence] = useState(false);
  const { user, token, refreshUserData } = useAuth();
  const { toast } = useToast();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showAddStudentForm, setShowAddStudentForm] = useState(false);
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [showTeacherSelector, setShowTeacherSelector] = useState(false);
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [allStudents, setAllStudents] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const queryClient = useQueryClient();
  const [teacherCategories, setTeacherCategories] = useState<any[]>([]);
  const [isLoadingTeacherCategories, setIsLoadingTeacherCategories] = useState(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInvoiceDropdownOpen, setIsInvoiceDropdownOpen] = useState(false);

  const [teacherName, setTeacherName] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

    const [showAddExtraForm, setShowAddExtraForm] = useState(false);

  const handleTimeChange = (newStartTime: string, newEndTime: string) => {
  const [startHours, startMinutes] = newStartTime.split(":").map(Number);
  const [endHours, endMinutes] = newEndTime.split(":").map(Number);

  const startDateTime = new Date(eventData.start);
  startDateTime.setHours(startHours, startMinutes, 0, 0);

  const endDateTime = new Date(eventData.end);
  endDateTime.setHours(endHours, endMinutes, 0, 0);
  

  // Calculer la durée en minutes
  let newDuration = Math.round((endDateTime.getTime() - startDateTime.getTime()) / 60000);

  // Si la durée est <= 0, ajuster startTime ou endTime
  if (newDuration <= 0) {
    newDuration = duration > 0 ? duration : 60; // Durée minimale de 60 minutes
    if (newEndTime < newStartTime) {
      // Si endTime est avant startTime, reculer startTime
      const correctedStartDateTime = new Date(endDateTime.getTime() - newDuration * 60000);
      const correctedStartTime = format(correctedStartDateTime, "HH:mm");
      setEventData({
        ...eventData,
        startTime: correctedStartTime,
        endTime: newEndTime,
      });
      setDuration(newDuration);
    } else {
      // Sinon, avancer endTime
      const correctedEndDateTime = new Date(startDateTime.getTime() + newDuration * 60000);
      const correctedEndTime = format(correctedEndDateTime, "HH:mm");
      setEventData({
        ...eventData,
        startTime: newStartTime,
        endTime: correctedEndTime,
      });
      setDuration(newDuration);
    }
  } else {
    // Mettre à jour normalement
    setEventData({
      ...eventData,
      startTime: newStartTime,
      endTime: newEndTime,
    });
    setDuration(newDuration);
  }
};


useEffect(() => {
  if (isInitialized) {
    // Délai de 500ms pour le loader
    const timer = setTimeout(() => setIsContentVisible(true), 10);
    return () => clearTimeout(timer);
  } else {
    setIsContentVisible(false);
  }
}, [isInitialized, open]);



  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleBackClick = () => {
    if (showAddStudentForm) {
      setShowAddStudentForm(false);
    } else if (showAddCategoryForm) {
      setShowAddCategoryForm(false);
    } else if (showAddLocationForm) {
      setShowAddLocationForm(false);
    } else if (showStudentSelector) {
      setShowStudentSelector(false);
    } else if (showTeacherSelector) {
      setShowTeacherSelector(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      
      setIsEditing(false);
      setIsInitialized(false);
      setSearchTerm('');
      setShowStudentSelector(false);
      showAddExtraForm && setShowAddExtraForm(false);

      if (event) {
        const startTime = format(new Date(event.start), 'HH:mm');
        const endTime = format(new Date(event.end), 'HH:mm');
        setEventData({
          id: event.id,
          title: event.title,
          start: new Date(event.start),
          end: new Date(event.end),
          startTime: startTime,
          endTime: endTime,
          studentIds: event.extendedProps?.studentIds || [],
          categoryId: event.extendedProps?.categoryId,
          locationId: event.extendedProps?.locationId || '',
          completed: isCompleted,
          note: event.extendedProps?.note || '',
        });
      }
    }
    onOpenChange(open);
  };

  useEffect(() => {
    const fetchTeacherCategories = async (teacherId: string) => {
      if (!teacherId) {
        setTeacherCategories([]);
        return;
      }

      setIsLoadingTeacherCategories(true);
      try {
        const response = await fetch(`${BASE_URL}/api/teacher/categories`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ teacherId })
        });

        if (!response.ok) {
          throw new Error('Failed to fetch teacher categories');
        }

        const data = await response.json();
        setTeacherCategories(data.categories || []);
      } catch (error) {
        console.error('Error fetching teacher categories:', error);
        setTeacherCategories([]);
      } finally {
        setIsLoadingTeacherCategories(false);
      }
    };

    if (open && isEditing && user.account_type === 'S' && invoiceType === 'T' && eventData?.teacherId && eventData.teacherId.length > 0) {
      fetchTeacherCategories(eventData.teacherId[0]);
    }
  }, [open, isEditing, user.account_type, invoiceType, eventData?.teacherId, token]);
  
  

  /*useEffect(() => {
    return () => {
      setIsEditing(false);
      setIsInitialized(false);
      console.log('Leave component');
    };
  }, []);*/

  const isStudentAbsentInStudentOffEvent = (studentId, lessonDate) => {
    return events.some(event => {
      if (event.type === 51 && event.allDay) {
        const eventStartDateStr = event.start.split('T')[0];
        const eventEndDateStr = event.end.split('T')[0];
        const lessonDateStr = moment(lessonDate).format('YYYY-MM-DD');

        const isStudentIncluded = event.extendedProps.studentIds.includes(studentId);

        const isDateInRange = lessonDateStr >= eventStartDateStr && lessonDateStr < eventEndDateStr;

        return isStudentIncluded && isDateInRange;
      }
      return false;
    });
  };

  const form = useForm({
    defaultValues: {
      studentIds: [],
      startDate: new Date(),
      endDate: new Date(),
      startTime: "09:00",
      endTime: "10:00",
      duration: 60,
      categoryId: "",
      locationId: "",
      note: ""
    }
  });

  const handleAddExtra = async (extra1: number, extra2: number, file1: File | null, file2: File | null) => {
  setIsSubmitting(true);
  try {
    const formData = new FormData();
    formData.append('eventId', eventData.id);
    formData.append('extra1', extra1.toString());
    formData.append('extra2', extra2.toString());
    formData.append('hasExtra1', (extra1 > 0).toString());
    formData.append('hasExtra2', (extra2 > 0).toString());
    formData.append('hasFile1', !!file1 ? 'true' : 'false');
    formData.append('hasFile2', !!file2 ? 'true' : 'false');

    if (file1) formData.append('file1', file1);
    if (file2) formData.append('file2', file2);

    const response = await fetch(`${BASE_URL}/api/event/extra`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to add extra expenses');
    }

    toast({
      title: "Frais supplémentaires ajoutés",
      description: "Les frais supplémentaires ont été ajoutés avec succès.",
    });

    setShowAddExtraForm(false);
    eventService.clearEventsCache();
    queryClient.clear();

    if (onRefreshEvents) {
      onRefreshEvents();
    }
  } catch (error) {
    console.error('Error adding extra expenses:', error);
    toast({
      title: "Erreur lors de l'ajout des frais",
      description: error instanceof Error ? error.message : "Veuillez réessayer",
      variant: "destructive",
    });
  } finally {
    setIsSubmitting(false);
  }
};

  const handleDeleteExtraFile = async (fileField: 'costs_1_file' | 'costs_2_file') => {
    try {
      const response = await fetch(`${BASE_URL}/api/event/extra/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: eventData.id,
          fileField,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      toast({
        title: t('common.success'),
        description: t('agenda.eventDeleted'), 
      });

      setEventData((prev: any) => ({
        ...prev,
        [fileField]: null,
      }));

      eventService.clearEventsCache();
      queryClient.clear();

      if (onRefreshEvents) {
        onRefreshEvents();
      }
    } catch (error) {
      console.error('Error deleting extra file:', error);
      toast({
        title: t('common.error'),
        variant: 'destructive',
      });
    }
  };

  const handleDateSelect = (date) => {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      setEventData({ ...eventData, start: formattedDate, end: formattedDate });
  };

  const formatDate = (dateString: string) => {
    const { locale } = useLanguage();
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const formatTime = (date, locale) => {
    if (locale === 'fr') {
      return format(new Date(date), 'HH\'h\'mm');
    } else {
      return format(new Date(date), 'h:mm a');
    }
  };

  const isSchoolStudent = (studentId) => {
    return students.some(student => student.id === studentId);
  };


  const formatEventDateRange = (startStr: string, endStr: string, locale: string): string => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    end.setSeconds(end.getSeconds() - 1);
  
    // 👉 Ne pas afficher la date de fin si elle est antérieure ou égale à la date de début
    if (!endStr || end <= start) {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(start);
    }
  
    const isSameDay =
      start.getDate() === end.getDate() &&
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();
  
    const isSameMonth =
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();
  
    const isSameYear = start.getFullYear() === end.getFullYear();
  
    const formatOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
    };
  
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });
    const yearFormatter = new Intl.DateTimeFormat(locale, { year: 'numeric' });
  
    if (isSameDay) {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(start);
    }
  
    if (isSameMonth) {
      const startDay = new Intl.DateTimeFormat(locale, formatOptions).format(start);
      const endDay = new Intl.DateTimeFormat(locale, formatOptions).format(end);
      const month = monthFormatter.format(start);
      const year = yearFormatter.format(start);
      return locale === 'fr' ? `Du ${startDay} au ${endDay} ${month} ${year}` : `From ${startDay} to ${endDay} ${month} ${year}`;
    }
  
    if (isSameYear) {
      const startDayMonth = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
      }).format(start);
      const endDayMonth = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
      }).format(end);
      const year = yearFormatter.format(start);
            return locale === 'fr'
        ? `Du ${startDayMonth} au ${endDayMonth} ${year}`
        : `From ${startDayMonth} to ${endDayMonth} ${year}`;
    }
  
    const fullStart = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(start);
  
    const fullEnd = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(end);
  
    return locale === 'fr' ? `Du ${fullStart} au ${fullEnd}` : `From ${fullStart} to ${fullEnd}`;
  };
  
  const hasCustomPrice = (teacherId: string, categoryId: string) => {
    //console.log('Teacher Prices edit:', teacherPrices);
    return teacherPrices.some(
      (teacherPrice) =>
        teacherPrice.teacher_id === parseInt(teacherId) &&
        teacherPrice.prices.some((price) => price.category_id === parseInt(categoryId))
    );
  };

  const getInclusiveEndDate = (end: Date, start: Date) => {
  // Si end > start → retire 1 jour
  if (end.getTime() > start.getTime()) {
    const inclusive = new Date(end);
    inclusive.setDate(inclusive.getDate() - 1);
    inclusive.setHours(0, 0, 0, 0);
    return inclusive;
  }
  return end;
};

const getInclusiveEndDateForDisplay = (end: Date, start: Date) => {
  if (end.getTime() > start.getTime()) {
    const inclusive = new Date(end);
    inclusive.setDate(inclusive.getDate() - 1);
    return inclusive;
  }
  return end;
};


  const getEventTypeDescription = (eventType) => {
    switch (eventType) {
      case 10:
        return t('agenda.Lesson');
      case 50:
          return t('agenda.coachAbsence');
      case 51:
        return t('agenda.studentAbsence');
      case 100:
        return t('agenda.Event');
      default:
        return t('agenda.unknownType');
    }
  };

  useEffect(() => {
    if (event && open && !isInitialized) {
      setIsEditing(false);
      setIsDropdownOpen(false);
      setShowDeleteConfirmation(false);

      const Owned = !!event.extendedProps.isEventOwned;
      setIsOwned(Owned);

      setIsTeacherEvent(event.extendedProps.isTeacherEvent);

      const invoice_type = event.extendedProps?.invoice_type || 'T';
      setInvoiceType(invoice_type);

      //console.log(invoiceType);
      
      const completed = !!event.extendedProps?.completed;
      setIsCompleted(completed);
      const allDay = !!event.allDay;
      setIsAllDay(allDay);

      const eventStart = new Date(event.start);
      // Pour les événements "all-day", `event.end` peut être null s'il dure un seul jour.
      // Dans ce cas, on utilise la date de début comme date de fin pour l'édition.
      const eventEnd = event.end ? new Date(event.end) : eventStart;

      const startTime = format(eventStart, 'HH:mm');
      const endTime = format(eventEnd, 'HH:mm');

      const teacherIds = (event.extendedProps.teachersIds?.length ?? 0) > 0
    ? event.extendedProps.teachersIds
    : [event.extendedProps.teacherId];


      setEventData({
        id: event.id,
        title: event.title,
        price: event.extendedProps?.price || 0,
        price_teacher: event.extendedProps?.price_teacher || 0,
        extra: event.extendedProps?.extra || '',
        costs_2: event.extendedProps?.costs_2 || 0,
        costs_1_file: event.extendedProps?.costs_1_file || null,
        costs_2_file: event.extendedProps?.costs_2_file || null,
        start: eventStart,
        end: eventEnd,
        startTime: startTime,
        endTime: endTime,
        studentIds: event.extendedProps?.studentIds || [],
        students: event.extendedProps?.students || [],
        categoryId: event.extendedProps?.categoryId,
        teacherId: teacherIds,
        locationId: event.extendedProps?.locationId || '',
        is_recurrent: event.extendedProps?.is_recurrent || false,
        original_event_id: event.extendedProps?.original_event_id || null,
        completed: completed,
        note: event.extendedProps?.note || '',
        isOwned: event.extendedProps?.isEventOwned || true,
      });
      
      if (teacherIds.length > 0) {
        const names = teacherIds.map((id: any) => {
          const t = teachers.find(teacher => teacher.id === id);
          return t ? `${t.firstname} ${t.lastname}` : '';
        }).filter(Boolean).join(', ');
        setTeacherName(names || event.extendedProps?.teacherName || null);
      } else {
        const teacher = teachers.find(t => t.id === event.extendedProps?.teacherId);
        if (teacher) {
          setTeacherName(`${teacher.firstname} ${teacher.lastname}`);
        } else {
          setTeacherName(event.extendedProps?.teacherName || null);
        }
      }

      const category = categories.find(c => c.id === event.extendedProps?.categoryId);

      const price = category && typeof category.price === 'number' ? category.price : 0;
      const studentCount = event.extendedProps?.studentIds?.length || 0;
      setTotalPrice(price * studentCount);

      if (event.start && event.end) {
        const calculatedDuration = eventService.calculateDuration(
          format(new Date(event.start), 'HH:mm'),
          format(new Date(event.end), 'HH:mm'),
          format(new Date(event.start), 'yyyy-MM-dd'),
          format(new Date(event.end), 'yyyy-MM-dd')
        );
        setDuration(calculatedDuration);
      }
        setTimeout(() => {
          setIsInitialized(true);
        }, 200);
    }
  }, [event, open, categories]);

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newDuration = parseInt(e.target.value) || 0;
      setDuration(newDuration);
  
      if (eventData.startTime) {
        const newEndTime = eventService.calculateEndTime(eventData.startTime, newDuration);
        setEventData({ ...eventData, endTime: newEndTime });
      }
    };

  useEffect(() => {
    setAllStudents(students);
  }, [students]);

  

  useEffect(() => {
    if (eventData && isEditing) {
      const teacher = teachers.find(c => c.id === eventData.teacherId);
      const category = categories.find(c => c.id === eventData.categoryId);
      if (category && eventData.studentIds && eventData.studentIds.length > 0 && duration > 0) {
        /*const price = eventService.calculatePrice(
          category,
          eventData.studentIds.length,
          duration
        );
        setTotalPrice(price);*/

        let price = 0;
              if (invoiceType === 'S' && eventData.teacherId && eventData.teacherId.length > 0) {
                const customPriceExists = hasCustomPrice(eventData.teacherId[0], eventData.categoryId);
                if (customPriceExists) {
                  const teacherPrice = teacherPrices.find(
                    tp => tp.teacher_id === parseInt(eventData.teacherId[0])
                  );
                  const priceData = teacherPrice.prices.find(
                    p => p.category_id === parseInt(eventData.categoryId)
                  );
        
                  if (priceData) {
                    const paymentType = selectedCategory.s_std_pay_type;
        
                    if (paymentType === 1) {
                      price = (priceData.prices.price_fix || 0) * eventData.studentIds.length;
                       setTotalPrice(price);
                    }
         
                    else {
                      let priceKey = `price_${Math.min(eventData.studentIds.length, 10)}`;
        
                      if (eventData.studentIds.length > 20) {
                        priceKey = 'price_su';
                      }
        
                      const hourlyRate = priceData.prices[priceKey] || priceData.prices.price_1 || 0;
        
                      price = parseFloat(hourlyRate) * eventData.studentIds.length * (duration / 60);
                      setTotalPrice(price);
                    }
                  }
                }
              }
              // Si aucun prix personnalisé n'est trouvé, utilisez le prix de la catégorie par défaut
              if (price === 0) {
                price = eventService.calculatePrice(selectedCategory, eventData.studentIds.length, duration);
                setTotalPrice(price);
              }
      } else {
        setTotalPrice(0);
      }
    }
  }, [eventData?.studentIds, eventData?.categoryId, categories, duration, isEditing]);

  useEffect(() => {
    if (eventData && eventData.startTime && eventData.endTime && eventData.start && eventData.end) {
      const calculatedDuration = eventService.calculateDuration(
        eventData.startTime,
        eventData.endTime,
        format(eventData.start, 'yyyy-MM-dd'),
        format(eventData.end, 'yyyy-MM-dd')
      );
      setDuration(calculatedDuration);
    }
  }, [eventData?.startTime, eventData?.endTime, eventData?.start, eventData?.end]);


  const handleRemoveCategory = () => {
    setEventData({...eventData, categoryId: undefined});
  };

  const handleRemoveLocation = () => {
    setEventData({...eventData, locationId: ''});
  };

  const handleRemoveTeacher = () => {
    setEventData({...eventData, teacherId: []});
  };

  const submitForm = async (resetAndClose: boolean) => {
    //console.log('test');
  }

  const handleAddStudent = async (firstname: string, lastname: string, email: string) => {
    if (!firstname.trim() || !lastname.trim()) {
      toast({
        title: t('agenda.studentNameRequired'),
        description: t('agenda.pleaseEnterFullName'),
        variant: "destructive"
      });
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/api/add_student`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          email: email.trim() || undefined
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create student');
      }
      const newStudent = await response.json();
      const newStudentWithName = {
        id: newStudent.student.id,
        firstname,
        lastname
      };
      handleStudentAdded(firstname, lastname, newStudent.student.id);
      setAllStudents(prev => [...prev, newStudentWithName]);

      // Mettre à jour l'état local avec le nouvel étudiant sélectionné
      const updatedStudentIds = [...(eventData.studentIds || []), newStudent.student.id];
      setEventData(prevEventData => ({
        ...prevEventData,
        studentIds: updatedStudentIds
      }));

      setShowAddStudentForm(false);
    } catch (error) {
      console.error('Error adding student:', error);
      toast({
        title: "Error adding student",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleAddCategory = async (name, color, paymentType, prices) => {
  if (!name.trim()) {
    toast({
      title: t('agenda.categoryNameRequired'),
      description: t('agenda.pleaseEnterName'),
      variant: "destructive"
    });
    return;
  }

  try {
    const response = await fetch(`${BASE_URL}/api/add_category`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        name: name.trim(),
        color: color,
        paymentType: paymentType,
        prices: prices
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add category');
    }

    const data = await response.json();
    //console.log("lacat", data);
    //await refreshUserData();

      setEventData({...eventData, categoryId: data.category.id});

      const newCategoryId = data.category.id;

      handleCategoryAdded(name, color, paymentType, prices, newCategoryId);

      setShowAddCategoryForm(false);

    /*toast({
      title: t('agenda.categoryAdded'),
      description: `${name} ${t('agenda.addedToCategoriesList')}`
    });*/

  } catch (error) {
    console.error('Error adding category:', error);
    toast({
      title: t('agenda.errorAddingCategory'),
      description: t('agenda.pleaseTryAgain'),
      variant: "destructive"
    });
  }
};


  const handleStudentStatusChange = async (eventId, studentId, newStatus) => {
      try {
        const updatedParticipation = await eventService.updateStudentEventStatus(token, { eventId, studentId, status: newStatus });
        const updatedStudents = eventStudents.map(student => {

          if (student.id == studentId) {
            return {
              ...student,
              participation_id: updatedParticipation.participation_id,
            };
          }
          return student;
        });
    
        setEventData({
          ...eventData,
          extendedProps: {
            ...eventData.extendedProps,
            students: updatedStudents,
          },
        });

        eventStudents.forEach(student => {
          if (student.id == studentId) {
            student.participation_id = updatedParticipation.participation_id;
          }
        });

        if (onRefreshEvents) {
          onRefreshEvents();
        }
        
        toast({
          title: "Student status successfully updated!",
          description: "Invoice status updated successfully",
        });
  
      } catch (error) {
        console.error('Error updating invoice status:', error);
      } finally {
        
      }
    };

  const handleAddLocation = async (name: string) => {
    try {
      const response = await fetch(`${BASE_URL}/api/add_location`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Échec de la création du lieu');
      }

      const newLocation = await response.json();

      setEventData({...eventData, locationId: newLocation.id});

      toast({
        title: "Lieu ajouté",
        description: "Le nouveau lieu a été ajouté avec succès.",
      });

      setShowAddLocationForm(false);

      if (onRefreshEvents) {
        onRefreshEvents();
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du lieu:', error);
      toast({
        title: "Erreur lors de l'ajout du lieu",
        description: error instanceof Error ? error.message : "Veuillez réessayer",
        variant: "destructive"
      });
    }
  };

  const handleSaveChanges = async (resetAndClose = false) => {
  setIsSubmitting(true);
  try {
    // Déterminez l'URL en fonction du type de compte de l'utilisateur
    await eventService.updateEvent(token, eventData.id, {
        ...eventData,
        duration: duration,
        totalPrice: totalPrice,
        saveAllRecurrence,
        startDate: format(new Date(eventData.start), 'yyyy-MM-dd'),
        endDate: format(new Date(eventData.end), 'yyyy-MM-dd'),
      }, user.account_type, invoiceType);

    if (onUpdate) {
      onUpdate(eventData);
    }

    toast({
      title: "Event updated",
      description: "Your changes have been saved successfully.",
    });

    setIsEditing(false);

    if (onRefreshEvents) {
      onRefreshEvents();
    }

    if (resetAndClose) {
      openNewLessonDialogWithCurrentValues();
    } else {
      // Fermer EventDetailModal
      onOpenChange(false);
      setIsInitialized(false);
    }

  } catch (error) {
    console.error('Error updating event:', error);
    toast({
      title: "INFORMATION",
      description: t('common.failedNetwork'),
      variant: "destructive"
    });
  } finally {
    setIsSubmitting(false);
  }
};

const openNewLessonDialogWithCurrentValues = () => {
  const startDate = format(new Date(eventData.start), 'yyyy-MM-dd');
  const endDate = isAllDay ? format(new Date(eventData.end), 'yyyy-MM-dd') : startDate;
  setNewLesson({
    title: eventData.title,
    studentIds: eventData.studentIds,
    categoryId: eventData.categoryId,
    locationId: eventData.locationId,
    teacherId: eventData.teacherId,
    startDate: startDate,
    endDate: endDate,
    startTime: eventData.startTime,
    endTime: eventData.endTime,
    isRecurring: eventData.is_recurrent,
    recurrenceEndDate: eventData.recurrenceEndDate,
    note: eventData.note,
    price: eventData.price,
    price2: eventData.price_teacher,
    extra: eventData.extra,
    formType: "lesson"
  });

  setShowNewLessonDialog(true);
  onOpenChange(false);
};

  const handleMarkAsCompleted = async () => {
    try {
      await eventService.markEventAsCompleted(token, event.id);

      setIsCompleted(true);
      setEventData({
        ...eventData,
        completed: true
      });

      if (eventService.updateEventInCache) {
        eventService.updateEventInCache(event.id, { completed: true });
      }

      if (onMarkComplete) {
        onMarkComplete(event.id);
      }

      toast({
        title: "Event marked as completed",
        description: "The event has been marked as completed successfully.",
      });

      if (onRefreshEvents) {
        onRefreshEvents();
      }
    } catch (error) {
      console.error('Error marking event as completed:', error);

      toast({
        title: "Error marking event as completed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleMarkAsUnCompleted = async () => {
    setIsProcessing(true);
    try {
      await eventService.markEventAsUncompleted(token, event.id);

      setIsCompleted(false);
      setEventData({
        ...eventData,
        completed: false
      });

      toggleDropdown();

      if (eventService.updateEventInCache) {
        eventService.updateEventInCache(event.id, { completed: false });
      }

      if (onUnmarkedComplete) {
        onUnmarkedComplete(event.id);
      }

      toast({
        title: "Event marked as unlocked",
        description: "The event has been marked as unlocked successfully.",
      });

      if (onRefreshEvents) {
        onRefreshEvents();
      }
    } catch (error) {
      console.error('Error marking event as unlocked:', error);

      toast({
        title: "Error marking event as unlocked",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    }
    finally {
      setIsProcessing(false);
    }
  };

  const navigateToInvoices = (studentId) => {
    if (studentId) {
      navigate(`/invoices/create/${studentId}`);
    } else if (eventStudents.length === 1) {
      navigate(`/invoices/create/${eventStudents[0].id}`);
    } else {
      navigate('/invoices/billing');
    }
    onOpenChange(false);
  };

  const getPaymentTypeLabel = (category: any): string => {
    if (!category) return '';
    return category.s_std_pay_type === 1 ? 'Fixed Price' : 'Hourly Rate';
  };

  const getPricePerStudent = (): number => {
    const category = categories.find(c => c.id === eventData?.categoryId);
    if (!category) return 0;
    return typeof category.price === 'number' ? category.price : 0;
  };

  const calculateTotalPrice = (): number => {
    if (!eventData) return 0;

    const category = categories.find(c => c.id === eventData.categoryId);
    if (!category) return 0;

    return totalPrice || 0;
  };


  const selectedCategory = (user.account_type === 'S' && invoiceType === "T")
    ? teacherCategories.find(cat => cat?.id === eventData?.categoryId)
    : categories.find(cat => cat?.id === eventData?.categoryId);


  const selectedLocation = locations.find(loc => loc?.id === eventData?.locationId);
  const selectedTeacher = teachers.find(teach => teach?.id === eventData?.teacherId);

  const ownSchool = user?.schools?.find(s => s.school_type === "C");
  const [selectedSchoolId, setSelectedSchoolId] = useState(() => {
    return ownSchool?.id || user?.schools?.[0]?.id || null;
  });

  const getFilteredStudents = () => {
    if (!searchTerm.trim()) {
      return allStudents;
    }

    const searchLower = searchTerm.toLowerCase();
    return allStudents.filter(student => {
      const fullName = `${student.firstname} ${student.lastname}`.toLowerCase();
      const displayName = student.name?.toLowerCase() || fullName;
      return displayName.includes(searchLower);
    });
  };

  const filteredStudents = useMemo(() => {
    const filteredStudentsRaw = getFilteredStudents();
    const ownSchool = user?.schools?.find(s => s.school_type === "C");

    if (
      user?.account_type === "C" &&
      user?.roleType === "teachers_all" &&
      selectedSchoolId
    ) {
      return filteredStudentsRaw.filter(student => {
        if (student.school_origin === "old") {
          return selectedSchoolId === ownSchool?.id;
        }
        return student.school_id === selectedSchoolId;
      });
    }
    return filteredStudentsRaw;
  }, [searchTerm, allStudents, selectedSchoolId, user]);

  const handleStudentSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchTerm.trim() !== '') {
      e.preventDefault();
      
      const filtered = getFilteredStudents();
      
      if (filtered && filtered.length > 0) {
        const firstMatchedStudent = filtered[0];
        const isAlreadySelected = eventData?.studentIds?.includes(firstMatchedStudent.id);
        
        if (!isAlreadySelected) {
          const updatedStudentIds = [...(eventData?.studentIds || []), firstMatchedStudent.id];
          setEventData({...eventData, studentIds: updatedStudentIds});
          setSearchTerm('');
        }
      }
    }
  };

  if (!event || !eventData) return null;

  const startTime = formatTime(event.start, locale);
  const endTime = formatTime(event.end, locale);
  const eventDate = format(new Date(event.start), 'EEEE, MMMM d, yyyy');
  const eventDateEnd = format(new Date(event.end), 'EEEE, MMMM d, yyyy');
  const isStartIsSameEnd = eventDate === eventDateEnd;
  
  
  const pref = user.teacher.validate_date_preference ?? 0;
  const eventEnd = event.end;
  const displayIsPast = event.extendedProps?.isEventPast;
  const isEventPast = event.extendedProps?.isEventPast;
  const remainingTime = event.extendedProps?.remainingTime || 0;

  const isEventCurrent = moment().isBetween(moment(event.start), moment(event.end));

  const category = event.extendedProps?.categoryId ?
    categories.find(c => c.id === event.extendedProps.categoryId) :
    undefined;

  const location = event.extendedProps?.locationId ?
    locations.find(l => l.id === event.extendedProps.locationId) :
    undefined;

  const price = event.extendedProps?.price;
  const price_teacher = event.extendedProps?.price_teacher;
  const price_per_teacher = event.extendedProps?.teachersWithPrices;
  const eventStudents = event.extendedProps?.students; //students.filter(s => event.extendedProps?.studentIds?.includes(s.id));
  const isEventLocked = event.extendedProps?.locked;


  const renderSelectedStudents = () => {
  if (!eventData?.studentIds?.length) {
    return <span className="text-muted-foreground">{t('students.selectStudents')}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1 max-w-full">
      {[...new Set(eventData.studentIds.map(id => String(id)))].map((id: string) => {
        const student = allStudents.find(s => String(s.id) === id);
        const isTeacherAll = user.roleType === "teachers_all";

        const isNotAllowed = !student; // 🔹 Simplifie la condition

        let displayName;
        if (isNotAllowed) {
          displayName = (
            <span className="text-gray-400">
              {isTeacherAll ? t('students.notAllowedForAll') : t('students.notAllowed')}
            </span>
          );
        } else {
          displayName = student.name || `${student.firstname} ${student.lastname}`;
        }

        return (
          <div key={id} className="flex items-center mr-1 mb-1">
            <Badge variant="secondary">{displayName}</Badge>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  const updatedStudentIds = eventData.studentIds.filter(studentId => String(studentId) !== id);
                  setEventData({ ...eventData, studentIds: updatedStudentIds });
                }}
                className="ml-1 text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="h-3 w-3" />
              </div>
          </div>
        );
      })}
    </div>
  );
};



  const renderStudentSelector = () => {
    const ownSchool = user?.schools?.find(s => s.school_type === "C");
    return (
    <div className="p-6 w-auto min-h-[38vh]">
      {/* Header */}
      <div className="flex justify-start items-center mb-4">
        <User className="mr-2" />
        <p className="text-lg font-semibold">
          {t('students.selectStudents')}{' '}
          {students.length > 0 ? `(${students.length})` : ''}
        </p>
      </div>

      <div className="grid gap-4">

        {/* SELECTOR SCHOOL si teachers_all */}
        {user?.account_type === "C" && user?.roleType === "teachers_all" && (
         <div className="relative bg-gray-50 p-2 rounded-md">
          <select
            value={selectedSchoolId || ""}
            onChange={(e) => setSelectedSchoolId(Number(e.target.value))}
            className="w-full border rounded-md p-2 pr-10 appearance-none"
          >
            {user?.schools?.map((school) => (
              <option key={school.id} value={school.id}>
                {school.school_name}{school.school_type === "C" ? " (" + t('students.myStudents') + ")" : ""}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        )}

        {/* Search + Add Student */}
        <div className="flex items-center justify-between gap-4 bg-gray-50 p-2 rounded-md">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder={t('agenda.searchStudent')} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleStudentSearchKeyDown}
              className="pl-8 w-48"
              autoFocus
            />
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowStudentSelector(false);
              setShowAddStudentForm(true);
            }}
            className="h-8 px-2"
          >
            <Plus className="h-3 w-3 mr-1" />
            {t('common.add')}
          </Button>
        </div>

        {/* Résultats */}
        <Label className="card">
          {searchTerm.trim()
            ? `${filteredStudents.length > 1
                ? `${filteredStudents.length} ${t('students.founds')}`
                : `${filteredStudents.length} ${t('students.found')}`}`
            : ''}
        </Label>

        {/* LISTE STUDENTS */}
        <div className="max-h-[50vh] overflow-y-auto border rounded-md" key={searchTerm}>
          {filteredStudents.map(student => {
            const isSelected = eventData?.studentIds?.includes(student.id);
            const displayName = student.name || `${student.firstname} ${student.lastname}`;

            return (
              <div
                key={student.id}
                className={`flex items-center p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-100 ${
                  isSelected ? 'bg-blue-50' : ''
                }`}
                onClick={() => {
                  const currentIds = eventData?.studentIds || [];
                  const newIds = isSelected
                    ? currentIds.filter(id => id !== student.id)
                    : [...currentIds, student.id];
                  setEventData({ ...eventData, studentIds: newIds });
                }}
              >
                <div className={`h-4 w-4 border rounded-sm mr-2 flex items-center justify-center ${
                  isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                }`}>
                  {isSelected && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className="flex-1">{displayName}</span>
                {user.account_type === "C" && user?.roleType === "teachers_all" && (
                  <div className="ml-2">
                    {student.school_origin === "old" ? <User /> : <Building />}
                  </div>
                )}
              </div>
            );
          })}

          {filteredStudents.length === 0 && (
            <div className="p-4 text-center text-muted-foreground">
              {t('agenda.noStudentFound')}
            </div>
          )}
        </div>

        {/* Footer */}
        {filteredStudents.length > 0 && (
          <div className="flex justify-end items-center space-x-2 mt-4">
            <Label className="flex">
              {t('students.selected')}: {eventData?.studentIds?.length || 0}
            </Label>
            <Button
              onClick={() => {
                setSearchTerm('');
                setShowStudentSelector(false);
              }}
              disabled={eventData?.studentIds?.length === 0}
            >
              {t('common.done')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
  };

  const renderMainContent = () => {

    if (showAddExtraForm) {
      return (
        <div className="h-full flex flex-col">
          <AddExtraForm
            eventData={eventData}
            onSubmit={handleAddExtra}
            onDeleteFile={handleDeleteExtraFile}
            onCancel={() => setShowAddExtraForm(false)}
          />
        </div>
      );
    }

    if (showStudentSelector) {
      return renderStudentSelector();
    }

    if (showAddStudentForm) {
      return (
        <div className="p-4">
          <AddStudentForm
            onSubmit={handleAddStudent}
            onCancel={() => setShowAddStudentForm(false)}
          />
        </div>
      );
    }

    if (showAddCategoryForm) {
      return (
        <div className="p-4">
          <AddCategoryForm
            onSubmit={handleAddCategory}
            onCancel={() => setShowAddCategoryForm(false)}
          />
        </div>
      );
    }

    if (showAddLocationForm) {
      return (
        <div className="p-4">
          <AddLocationForm
            onSubmit={handleAddLocation}
            onCancel={() => setShowAddLocationForm(false)}
          />
        </div>
      );
    }

    if (showTeacherSelector) {
      return (
        <TeacherSelector
          teachers={teachers}
          selectedTeacherIds={eventData.teacherId}
          onTeacherIdsChange={(ids) => setEventData({ ...eventData, teacherId: ids })}
          onClose={() => setShowTeacherSelector(false)}
        />
      );
    }

    return (
      <>
        <ScrollArea className={`${isMobile ? "max-h-[calc(100dvh-180px)]" : "max-h-[60vh]"} overflow-auto`}>
          {!isEditing ? (
            <div className={`${isMobile ? "px-4" : ""} py-4 space-y-6`}>

              {user.account_type === 'C' && user.roleType === "teachers_all" && !isOwned && isTeacherEvent && (
                <div className="flex items-center text-xs text-muted-foreground">
                  <InfoIcon className="h-3 w-3 mr-1" />
                    <span>{t('agenda.createBySchool')}</span>
                  </div>
              )}

              {user.account_type === 'C' && user.roleType === "teachers_all" && !isOwned && isTeacherEvent && event.extendedProps.no_of_teachers > 1 &&  (
                <div className="flex items-center text-xs text-muted-foreground">
                  <InfoIcon className="h-3 w-3 mr-1" />
                    <span>{t('agenda.eventMultiTeachers')}: {event.extendedProps.teacherName}</span>
                  </div>
              )}

              {user.account_type === 'C' && user.roleType === "teachers_all" && !isOwned && !isTeacherEvent && (
                <div className="flex items-center text-xs text-muted-foreground">
                  <InfoIcon className="h-3 w-3 mr-1" />
                    <span>{t('agenda.eventForOtherCoach')}</span>
                  </div>
              )}

              {event.title && (
                <>
                  <div className="flex items-center space-x-2">
                    <Pencil className="h-5 w-5 text-muted-foreground" />
                    <span>{event.title}</span>
                  </div>
                </>
              )}

              {(user.account_type === 'S' && teacherName && event.extendedProps.type != 51) || (user.account_type === 'C' && user.roleType === "teachers_all" && teacherName && !isTeacherEvent && event.extendedProps.type != 51) ? (
                <div className="flex items-center space-x-2 font-semibold">
                  <GraduationCap className="h-5 w-5 text-muted-foreground" />
                  <span>{teacherName}</span>
                </div>
              ):(null)}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center space-x-2">
                  <CalendarIcon className="h-5 w-5 text-muted-foreground"/>
                  <span>{formatEventDateRange(eventDate, eventDateEnd, locale)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {displayIsPast && !isEventCurrent && (
                    <Badge key="past-incoming" variant="secondary">
                      <span>{displayIsPast ? t('agenda.isCompleted') : 'Upcoming'}</span>
                    </Badge>
                  )}
                  {isEventCurrent && (
                    <Badge key="current" variant="secondary" className='bg-green-50 border-green-200 text-green-500'>
                      <span>{t('agenda.inProgress')}</span>
                    </Badge>
                  )}
                  {!displayIsPast && !isEventCurrent && (
                    <Badge key="past-incoming" variant="secondary">
                      <span>
                        {remainingTime ? (
                          locale === 'fr' ? (
                            remainingTime.days > 0 || remainingTime.hours > 0 || remainingTime.minutes > 0 ? (
                              `Dans ${remainingTime.days > 0 ? `${remainingTime.days} jour${remainingTime.days > 1 ? 's' : ''}` : ''}${remainingTime.days > 0 && (remainingTime.hours > 0 || remainingTime.minutes > 0) ? ', ' : ''}${remainingTime.hours > 0 ? `${remainingTime.hours} heure${remainingTime.hours > 1 ? 's' : ''}` : ''}${remainingTime.hours > 0 && remainingTime.minutes > 0 ? ' et ' : ''}${remainingTime.minutes > 0 ? `${remainingTime.minutes} minute${remainingTime.minutes > 1 ? 's' : ''}` : ''}`
                            ) : 'In progress'
                          ) : (
                            remainingTime.days > 0 || remainingTime.hours > 0 || remainingTime.minutes > 0 ? (
                              `In ${remainingTime.days > 0 ? `${remainingTime.days} day${remainingTime.days > 1 ? 's' : ''}` : ''}${remainingTime.days > 0 && (remainingTime.hours > 0 || remainingTime.minutes > 0) ? ', ' : ''}${remainingTime.hours > 0 ? `${remainingTime.hours} hour${remainingTime.hours > 1 ? 's' : ''}` : ''}${remainingTime.hours > 0 && remainingTime.minutes > 0 ? ' and ' : ''}${remainingTime.minutes > 0 ? `${remainingTime.minutes} minute${remainingTime.minutes > 1 ? 's' : ''}` : ''}`
                            ) : ''
                          )
                        ) : 'In progress'}
                      </span>
                    </Badge>
                  )}
                </div>
              </div>

              {!isAllDay && (
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-muted-foreground"/>
                  <span>{startTime} - {endTime}</span>
                  <Badge key="duration" variant="secondary">{duration ? `${duration} mn` : 'N/A'}</Badge>
                </div>
              )}

              {location && (
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-muted-foreground"/>
                  <span>{location.name}</span>
                </div>
              )}

              {!category && event.extendedProps.type === 10 && ((user.account_type === 'C' && event.extendedProps.invoice_type === 'S') ||
                  (user.account_type === 'S' && event.extendedProps.invoice_type === 'T')) && (
                <div className="flex items-center space-x-2">

                 {user.account_type === 'S' && user.roleType === "school_admin" ? (
                      <Tag className="w-4 h-4" />
                  ) : (
                      <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: event.extendedProps?.color }}></div>
                  )}

                  <span>
                    {event.extendedProps?.category || 'N/A'}
                  </span>
                </div>
              )}

              {category && (
                <div className="flex items-center space-x-2">
                    {user.account_type === 'S' && user.roleType === "school_admin" ? (
                        <Tag className="w-4 h-4" />
                    ) : (
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }}></div>
                    )}

                  <span>
                    {category.name}
                    {user.account_type === 'C' && (
                    <Badge key="price-category" variant="secondary" className="ml-2">
                      <span className="space-x-2">{price ? `${price.toFixed(2)}` : '0'} {user.currency_code} {eventStudents && eventStudents.length > 0 && (
                         <>
                         / {t('students.student')} 
                         </>
                      )}</span>
                    </Badge>
                    )}
                  </span>
                </div>
              )}

              {category && (
                <div>
                  {eventStudents && eventStudents.length > 0 && user.account_type === 'S' && (
                  <span>
                    <Badge key="price-category" variant="secondary" className='mr-2 mb-3'>
                      <span className="space-x-1 flex"><HandCoins size={16} className="text-muted-foreground mr-2" /> {price ? `${price.toFixed(2)}` : '0'} {user.currency_code} 
                         <>
                         <span className="text-muted-foreground">/{t('students.student')}</span>
                         </>
                      </span>
                    </Badge>
                  </span>
                  )}



                  <span>
                    {user.account_type === 'S' && invoiceType === 'S' && event.extendedProps.no_of_teachers < 2 && (
                    <Badge key="price-category" variant="secondary" className='mr-2'>
                      <span className="space-x-1 flex"><HandCoins size={16} className="text-muted-foreground mr-2" /> {price_teacher ? `${price_teacher.toFixed(2)}` : '0'} {user.currency_code}
                         <>
                          <span className="text-muted-foreground">/{t('teachers.teacher')}</span>
                         </>
                      </span>
                    </Badge>
                    )}
                  </span>




                  {/*<span>
                    {user.account_type === 'S' && invoiceType === 'S' && event.extendedProps.no_of_teachers > 1 && (
                      <div>
                        {price_per_teacher?.map((teacher) => (
                          <Badge key={teacher.id} variant="secondary" className='mr-2'>
                            <span className="flex items-center">
                              <HandCoins size={16} className="text-muted-foreground mr-2" />
                              
                              <span>
                                {teacher.name} : {teacher.buy_price?.toFixed(2) ?? '0'} {user.currency_code}
                              </span>

                            </span>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </span>*/}



                 
                  {user.account_type === 'S' && invoiceType === 'S' && event.extendedProps.no_of_teachers > 1 && (
                    <>
                      <table className="ml-0 mt-3 border border-border rounded-md overflow-hidden text-sm w-full">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs">{t('teachers.teacher')}</th>
                            <th className="px-3 py-2 text-right text-xs">
                              <span className="inline-flex items-center justify-end w-full">
                                <HandCoins size={14} className="text-muted-foreground mr-1" />
                                /{t('teachers.teacher')}
                              </span>
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {price_per_teacher?.map((teacher) => (
                            <tr key={teacher.id} className="border-t">
                              <td className="px-3 py-2">
                                {teacher.name}
                              </td>

                              {/* Prix teacher (buy_price) */}
                              <td className="px-3 py-2 flex items-center inline-flex items-center justify-end w-full">
                                {teacher.buy_price?.toFixed(2) ?? '0'} {user.currency_code}
                              </td>

                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
            



                </div>
              )}


              {/*!isAllDay &&
                eventStudents &&
                eventStudents.length > 0 &&
                !(
                  user.account_type === 'S' &&
                  event.extendedProps.invoice_type === 'T'
                ) && 
                !(
                  user.account_type === 'C' &&
                  event.extendedProps.invoice_type === 'S'
                ) && (
                  <div className="flex items-center space-x-2 h-10">
                    <HandCoins className="h-5 w-5 text-muted-foreground" />
                    <Badge key="price-category" variant="primary">
                      TOTAL: {price && eventStudents.length
                        ? `${(Number(price) * eventStudents.length).toFixed(2)}`
                        : 'N/A'} {user.currency_code}
                    </Badge>
                  </div>
              )*/}

              {!isAllDay &&
                eventStudents &&
                eventStudents.length > 0 &&
                !(
                  user.account_type === 'S' 
                ) && 
                !(
                  user.account_type === 'C' &&
                  event.extendedProps.invoice_type === 'S'
                ) && (
                  <div className="flex items-center space-x-2 h-10">
                    <HandCoins className="h-5 w-5 text-muted-foreground" />
                    <Badge key="price-category" variant="primary">
                      TOTAL: {price && eventStudents.length
                        ? `${(Number(price) * eventStudents.length).toFixed(2)}`
                        : '0'} {user.currency_code}
                    </Badge>
                  </div>
              )}


              {isAllDay && event && event.extendedProps && event.extendedProps.type === '100' && isOwned && (
                <div className="flex items-center space-x-2 h-10">
                  <HandCoins className="h-5 w-5 text-muted-foreground"/>
                 <Badge key="price-category" variant="primary">
                    TOTAL: {price && eventStudents.length ? `${(Number(price)*eventStudents.length).toFixed(2)}` : 'N/A'} {user.currency_code}
                 </Badge>
                </div>
              )}

              {isAllDay && (event && event.extendedProps && event.extendedProps.price > 0 || event && event.extendedProps && event.extendedProps.extra > 0) && (isOwned) && (
                <div className="flex items-center space-x-2 h-10">
                  <HandCoins className="h-5 w-5 text-muted-foreground"/>
                  <Badge key="price-category" variant="primary">
                    {user.account_type === 'S' ? t('categories.price_per_student') : 'TOTAL'}: {
                      user.currency_code === 'EUR'
                        ? `${Number(event.extendedProps.price + event.extendedProps.extra)} ${user.currency_code}`
                        : `${user.currency_code} ${Number(event.extendedProps.price + event.extendedProps.extra)}`
                    }
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    (
                    {event.extendedProps.price > 0 && (
                      <>
                        {t('agenda.createNew.price')}: {
                          user.currency_code === 'EUR'
                            ? `${event.extendedProps.price} ${user.currency_code}`
                            : `${user.currency_code} ${event.extendedProps.price}`
                        }
                      </>
                    )}
                    {event.extendedProps.price > 0 && event.extendedProps.extra > 0 && ', '}
                    {event.extendedProps.extra > 0 && (
                      <>
                        {t('agenda.createNew.extra')}: {
                          user.currency_code === 'EUR'
                            ? `${event.extendedProps.extra} ${user.currency_code}`
                            : `${user.currency_code} ${event.extendedProps.extra}`
                        }
                      </>
                    )}
                    )
                  </span>
                </div>
              )}


              {isAllDay && (event && event.extendedProps && event.extendedProps.price > 0 || event && event.extendedProps && event.extendedProps.extra > 0) && user.roleType === "teachers_all" && (isOwned || isTeacherEvent) && event.extendedProps.invoice_type === 'S' && (
                <div className="flex items-center space-x-2 h-10">
                  <HandCoins className="h-5 w-5 text-muted-foreground"/>
                  <Badge key="price-category" variant="primary">
                    {user.account_type === 'S' ? t('categories.price_per_student') : 'TOTAL'}: {
                      user.currency_code === 'EUR'
                        ? `${Number(event.extendedProps.price_teacher + event.extendedProps.extra + event.extendedProps.costs_2)} ${user.currency_code}`
                        : `${user.currency_code} ${Number(event.extendedProps.price_teacher + event.extendedProps.extra + event.extendedProps.costs_2)}`
                    }
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    (
                    {event.extendedProps.price_teacher > 0 && (
                      <>
                        {t('agenda.createNew.price')}: {
                          user.currency_code === 'EUR'
                            ? `${event.extendedProps.price_teacher} ${user.currency_code}`
                            : `${user.currency_code} ${event.extendedProps.price_teacher}`
                        }
                      </>
                    )}
                    {event.extendedProps.price_teacher > 0 && event.extendedProps.extra > 0 && ', '}
                    {event.extendedProps.extra > 0 && (
                      <>
                        <span className="inline-flex items-center gap-1">
                          {t('agenda.createNew.extra')}: {
                            user.currency_code === 'EUR'
                              ? `${event.extendedProps.extra} ${user.currency_code}`
                              : `${user.currency_code} ${event.extendedProps.extra}`
                          }
                          {event.extendedProps.costs_1_file && (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-3.5 w-3.5 text-blue-500 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="p-1 border bg-white shadow-xl">
                                  <img 
                                    src={`${BASE_URL}/storage/${event.extendedProps.costs_1_file}`} 
                                    alt="Receipt" 
                                    className="max-w-[200px] max-h-[200px] object-contain rounded-sm"
                                  />
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                      </>
                    )}

                    {event.extendedProps.price_teacher > 0 && event.extendedProps.costs_2 > 0 && ', '}

                    {event.extendedProps.costs_2 > 0 && (
                      <>
                        {t('agenda.createNew.extra')}: {
                          user.currency_code === 'EUR'
                            ? `${event.extendedProps.costs_2} ${user.currency_code}`
                            : `${user.currency_code} ${event.extendedProps.costs_2}`
                        }
                      </>
                    )}

                    )
                  </span>
                </div>
              )}






              {isAllDay && user.account_type === 'S' && (event && event.extendedProps && event.extendedProps.price_teacher > 0 || event && event.extendedProps && event.extendedProps.extra > 0) && isOwned && (
                 <>
                      <table className="ml-0 mt-3 border border-border rounded-md overflow-hidden text-sm w-full">
  <thead className="bg-muted">
    <tr>
      <th className="px-3 py-2 text-left text-xs">
        {t('teachers.teacher')}
      </th>

      <th className="px-3 py-2 text-right text-xs">
        <span className="inline-flex items-center justify-end w-full">
          {t('teachers.teacher')}
        </span>
      </th>

      {/* Colonne Frais 1 */}
      {price_per_teacher?.some(teacher => teacher.costs_1 != null && teacher.costs_1 > 0) && (
        <th className="px-3 py-2 text-right text-xs">
          <div className="flex items-center justify-end gap-1">
            {t('agenda.charges')}
            {event.extendedProps.costs_1_file && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-blue-500 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="p-1 border bg-white shadow-xl">
                    <img 
                      src={event.extendedProps.costs_1_file} 
                      alt="Receipt" 
                      className="max-w-[200px] max-h-[200px] object-contain rounded-sm"
                    />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </th>
      )}

      {/* Colonne Frais 2 */}
      {price_per_teacher?.some(teacher => teacher.costs_2 != null && teacher.costs_2 > 0) && (
        <th className="px-3 py-2 text-right text-xs">
          <div className="flex items-center justify-end gap-1">
            {t('agenda.extraCharges')}
            {event.extendedProps.costs_2_file && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-blue-500 cursor-help" /> 
                  </TooltipTrigger>
                  <TooltipContent side="top" className="p-1 border bg-white shadow-xl">
                    <img 
                      src={event.extendedProps.costs_2_file} 
                      alt="Receipt" 
                      className="max-w-[200px] max-h-[200px] object-contain rounded-sm"
                    />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </th>
      )}
    </tr>
  </thead>

  <tbody>
    {price_per_teacher?.map((teacher) => (
      <tr key={teacher.id} className="border-t">
        <td className="px-3 py-2">
          {teacher.name}
        </td>

        {/* Prix teacher */}
        <td className="px-3 py-2 text-right">
          {teacher.buy_price?.toFixed(2) ?? '0'} {user.currency_code}
        </td>

        {/* Valeur Frais 1 */}
        {price_per_teacher?.some(t => t.costs_1 != null && t.costs_1 > 0) && (
          <td className="px-3 py-2 text-right">
            {teacher?.costs_1 != null && teacher.costs_1 > 0 && (
              <div className="flex items-center justify-end gap-1">
                {teacher.costs_1.toFixed(2)} {user.currency_code}
                {(teacher.costs_1_file || event.extendedProps.costs_1_file) && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-blue-500 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="p-1 border bg-white shadow-xl">
                        <img 
                          src={event.extendedProps.costs_1_file} 
                          alt="Receipt" 
                          className="max-w-[200px] max-h-[200px] object-contain rounded-sm"
                        />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
          </td>
        )}

        {/* Valeur Frais 2 */}
        {price_per_teacher?.some(t => t.costs_2 != null && t.costs_2 > 0) && (
          <td className="px-3 py-2 text-right">
            {teacher?.costs_2 != null && teacher.costs_2 > 0 && (
              <div className="flex items-center justify-end gap-1">
                {teacher.costs_2.toFixed(2)} {user.currency_code}
                {(teacher.costs_2_file) && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-blue-500 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="p-1 border bg-white shadow-xl">
                        <img 
                          src={teacher.costs_2_file}
                          alt="Receipt" 
                          className="max-w-[200px] max-h-[200px] object-contain rounded-sm"
                        />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
          </td>
        )}
      </tr>
    ))}
  </tbody>
</table>
                    </>
              )}




              {event && event.extendedProps && event.extendedProps.type !== 50 && (eventStudents && eventStudents.length === 0) && (
                <div className="p-4 bg-blue-50 rounded-md border border-gray-200">
                   <div className="space-y-2">
                    <span className='text-sm'>{t('agenda.settingRequired')}</span>
                   </div>
                </div>
              )}

              {event && event.extendedProps && event.extendedProps.type !== 50 && (eventStudents && eventStudents.length > 0) && (
              <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                {eventStudents && eventStudents.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5 text-muted-foreground"/>
                      <span><strong>{t('students.students')}:</strong></span>
                    </div>
                    <div className="flex flex-wrap gap-1 ml-7">
                      {eventStudents.map(student => (                
                      <div className="mb-2" key={student.id}>

                        {/*isStudentAbsentInStudentOffEvent(student.id) && (
                          <Badge variant="destructive" className="ml-2">
                            Absent
                          </Badge>
                        )*/}

                        {!isSchoolStudent(student.id) && (
                          <Badge
                            key={student.id}
                            className="text-xs cursor-pointer flex items-center"
                            variant="secondary"
                          >
                          <span className='flex'>
                            <span className='pr-1'>
                              {student.name}
                            </span> 
                            {/*<TooltipProvider>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                    <Info size={15} />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{user?.account_type === "S" ? t('students.coachStudentsDesc') : t('students.cochStudentDeleted')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>*/}
                          </span>
                          </Badge>
                        )}

                        {isSchoolStudent(student.id) && (
                        <Popover>
                          <PopoverTrigger asChild>
                          <Badge
                            key={student.id}
                            className="text-xs cursor-pointer flex items-center"
                            variant="secondary"
                          >
                           {(student.participation_id == 200 || student.participation_id == 10 || student.participation_id == 0) && !isStudentAbsentInStudentOffEvent(student.id, eventData.start) ? (
                              <span>
                                {student.name} 
                              </span>
                            ) : (
                              <span className="text-muted-foreground">
                                {student.name} {student.status === 'deleted' ? '' : '(Absent)'}
                              </span>
                            )}
                            {!isStudentAbsentInStudentOffEvent(student.id, eventData.start) && event.extendedProps.type !== 51 && !isCompleted && (
                            <ChevronDown className="ml-1 h-3 w-3" />
                            )}
                          </Badge>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            {!isStudentAbsentInStudentOffEvent(student.id, eventData.start) && event.extendedProps.type !== 51 && !isCompleted && (
                            <div className="flex flex-col m-3 space-y-3">
                              {/* Statut actuel avec icône et badge couleur */}
                              <div className="flex items-center space-x-2 text-sm">
                                <span className={`text-xs ${(student.participation_id == 200 || student.participation_id == 10 || student.participation_id == 0) && !isStudentAbsentInStudentOffEvent(student.id, eventData.start) ? 'text-green-600' : 'text-red-600'}`}>
                                  {(student.participation_id == 200 || student.participation_id == 10 || student.participation_id == 0) && !isStudentAbsentInStudentOffEvent(student.id, eventData.start) ? '✅' : '⛔️'}
                                </span>
                                <span className="text-gray-700">
                                  {' '}
                                  <span className={`text-sm ${(student.participation_id == 200 || student.participation_id == 10 || student.participation_id == 0) && !isStudentAbsentInStudentOffEvent(student.id, eventData.start) ? 'text-green-600' : 'text-red-600'}`}>
                                    {(student.participation_id == 200 || student.participation_id == 10 || student.participation_id == 0) && !isStudentAbsentInStudentOffEvent(student.id, eventData.start) ? t('agenda.present') : t('agenda.absent')}
                                  </span>
                                </span>
                              </div>

                              {/* Bouton d’action stylé */}
                              <button
                                onClick={() =>
                                  handleStudentStatusChange(
                                    eventData.id,
                                    student.id,
                                    (student.participation_id == 200 || student.participation_id == 10 || student.participation_id == 0) && !isStudentAbsentInStudentOffEvent(student.id, eventData.start) ? '199' : '200'
                                  )
                                }
                                className={`self-start text-sm px-3 py-1 rounded-full transition-colors duration-150 ${
                                  (student.participation_id == 200 || student.participation_id == 10 || student.participation_id == 0) && !isStudentAbsentInStudentOffEvent(student.id, eventData.start)
                                    ? 'text-red-700 hover:bg-red-100'
                                    : 'text-green-700 hover:bg-green-100'
                                }`}
                              >
                                {(student.participation_id == 200 || student.participation_id == 10 || student.participation_id == 0) && !isStudentAbsentInStudentOffEvent(student.id, eventData.start) ? t('agenda.markAsAbsent') : t('agenda.markAsPresent')}
                              </button>
                            </div>
                            )}
                          </PopoverContent>
                        </Popover>
                        )}
                      </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )}

              {event.extendedProps?.note && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-muted-foreground"/>
                    <span><strong>Note:</strong></span>
                  </div>
                  <div className="ml-7 text-sm border-l-2 pl-3 py-1 border-muted">
                    {event.extendedProps.note}
                  </div>
                </div>
              )}

              {/*<div className="flex items-center space-x-2">
                {isEventLocked && (
                  <Lock className="h-5 w-5 text-muted-foreground" />
                )}
                {isCompleted && (
                  <Badge className="bg-green-100 text-sm"><Lock className="h-4 w-4 mr-1 text-dark" /> {t('agenda.completed')}</Badge>
                )}
              </div>*/}
            </div>
          ) : (
            <div className={`grid gap-4 py-4 ${isMobile ? "px-4" : ""}`}>

              {event && event.extendedProps && (event.extendedProps.type === 100 || event.extendedProps.type === 10 || event.extendedProps.type === 51) && (
              <div className="grid gap-2 bg-gray-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                  <Label htmlFor="student">{t('agenda.students')}</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddStudentForm(true)}
                    className="h-8 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> {t('agenda.addNew')}
                  </Button>
                </div>

                <Button
                  variant="outline"
                  className="w-full justify-start text-left h-auto min-h-10 py-2"
                  onClick={() => setShowStudentSelector(true)}
                >
                  {renderSelectedStudents()}
                </Button>
              </div>
              )}




            {user.account_type === 'S' && isOwned && event.extendedProps.type !== 51 && (
              invoiceType === 'T' ? (
                <div className="grid gap-2 bg-gray-50 rounded-md p-4 relative">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="teacher">{t('agenda.teacher')}</Label>
                  </div>
                  {(() => {
                    const currentTeacherId = Array.isArray(eventData.teacherId) && eventData.teacherId.length > 0 
                      ? eventData.teacherId[0] 
                      : (typeof eventData.teacherId === 'string' || typeof eventData.teacherId === 'number' ? eventData.teacherId : null);
                    
                    const teacher = teachers.find(t => String(t.id) === String(currentTeacherId));

                    if (teacher) {
                      return (
                        <div className="flex items-center gap-2">
                          <Badge className="flex items-center gap-2">
                            <User className="w-3 h-3" />
                            <span>{teacher.firstname} {teacher.lastname}</span>
                          </Badge>
                          <button
                            type="button"
                            className="ml-1 rounded-full outline-none hover:bg-muted"
                            onClick={handleRemoveTeacher}
                          >
                            <X className="h-3 w-3" />
                            <span className="sr-only">Remove</span>
                          </button>
                        </div>
                      );
                    } else {
                      return (
                        <Select
                          value={currentTeacherId ? String(currentTeacherId) : ""}
                          onValueChange={(value) => setEventData({...eventData, teacherId: [value]})}
                        >
                          <SelectTrigger id="teacher">
                            <SelectValue placeholder={t('agenda.selectTeacher')} />
                          </SelectTrigger>
                          <SelectContent>
                            {teachers.map((teacher) => (
                              <SelectItem key={teacher.id} value={String(teacher.id)}>
                                {teacher.firstname} {teacher.lastname}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }
                  })()}
                </div>
              ) : (
                <div className="grid gap-2 bg-gray-50 rounded-md p-4 relative">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="teacher">{t('agenda.teachers')}</Label>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full justify-start text-left h-auto min-h-10 py-2"
                    onClick={() => setShowTeacherSelector(true)}
                  >
                    {eventData.teacherId && eventData.teacherId.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-full">
                        {eventData.teacherId.map((id: any) => {
                          const teacher = teachers.find(t => t.id === id);
                          if (!teacher) return null;
                          return (
                            <div key={id} className="flex items-center mr-1 mb-1">
                              <Badge variant="secondary">
                                {teacher.firstname} {teacher.lastname}
                              </Badge>
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updatedTeacherIds = eventData.teacherId.filter((tid: any) => tid !== id);
                                  setEventData({ ...eventData, teacherId: updatedTeacherIds });
                                }}
                                className="ml-1 text-muted-foreground hover:text-foreground cursor-pointer"
                              >
                                <X className="h-3 w-3" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{t('agenda.selectTeachers')}</span>
                    )}
                  </Button>
                </div>
              )
            )}




              {/*user.account_type === 'S' && isOwned && invoiceType === "S" && !event.allDay && (
                <div className="grid gap-2 bg-blue-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                  {!selectedCategory ? (
                  <Label className='text-xs' htmlFor="category">{t('agenda.noSelectedCategory')}</Label>
                  ) : (
                  <Label className='text-xs' htmlFor="category">{t('agenda.schoolCategory')}</Label>
                  )}
                </div>
                </div>
              )*/}
              {/*user.account_type === 'S' && isOwned && invoiceType === "T" && !event.allDay && (
                <div className="grid gap-2 bg-blue-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                  {!selectedCategory ? (
                  <Label className='text-xs' htmlFor="category">{t('agenda.noSelectedCategory')}</Label>
                  ) : (
                  <Label className='text-xs' htmlFor="category">{t('agenda.schoolForCoachCategory')}</Label>
                  )}
                </div>
                </div>
              )*/}
              
              {/*user.account_type === 'S' && !isOwned && (
                <div className="grid gap-2 bg-blue-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                  {!selectedCategory ? (
                  <Label className='text-xs' htmlFor="category">{t('agenda.eventCreatedByTeacher')}</Label>
                  ) : (
                  <Label className='text-xs' htmlFor="category">{t('agenda.youCreatedEventButYouCantChangeCategory')}</Label>
                  )}
                </div>
                </div>
              )*/}

              {/*user.account_type === 'C' && user.roleType === "teachers_all" && invoiceType === "T" && (
                <div className="grid gap-2 bg-blue-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                 <Label className='text-xs' htmlFor="category">{t('agenda.categoryChoosenByTeacher')}</Label>
                </div>
                </div>
              )*/}
              {/*user.account_type === 'C' && user.roleType === "teachers_all" && invoiceType === "S" && (
                <div className="grid gap-2 bg-blue-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                  <Label className='text-xs' htmlFor="category">{t('agenda.schoolCategory')}</Label>
                </div>
                </div>
              )*/}

              {!isAllDay && user.account_type === 'S' && invoiceType === "T" && (
              <div className="grid gap-2 bg-gray-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                  <Label htmlFor="category">{t('agenda.category')}</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddCategoryForm(true)}
                    className="h-8 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> {t('agenda.addNew')}
                  </Button>
                </div>

                {selectedCategory ? (
                  <div className="flex items-center gap-2" key={selectedCategory.id}>
                    <Badge className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedCategory.color }}></div>
                      {selectedCategory.name}
                    </Badge>
                    <button
                      type="button"
                      className="ml-1 rounded-full outline-none hover:bg-muted"
                      onClick={handleRemoveCategory}
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove</span>
                    </button>
                  </div>
                ) : (
                  <Select
                    value={eventData.categoryId || 'placeholder'}
                    onValueChange={(value) => {
                      if (value === 'placeholder') return;
                      setEventData({...eventData, categoryId: value});
                    }}
                    disabled={isLoadingTeacherCategories}
                  >
                    <SelectTrigger id="category">
                      {(() => {
                        const selected = teacherCategories.find(c => c.id === eventData.categoryId);
                        if (selected) {
                          return (
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selected.color }}></div>
                              {selected.name}
                            </div>
                          )
                        }
                        return isLoadingTeacherCategories ? t('common.loading') : t('agenda.selectCategory');
                      })()}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder" disabled>
                        {isLoadingTeacherCategories ? t('common.loading') : t('agenda.selectCategory')}
                      </SelectItem>
                      {!isLoadingTeacherCategories && teacherCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                            {category.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              )}

              {!isAllDay && user.account_type === 'S' && invoiceType === "S" && (
              <div className="grid gap-2 bg-gray-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                  <Label htmlFor="category">{t('agenda.category')}</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddCategoryForm(true)}
                    className="h-8 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> {t('agenda.addNew')}
                  </Button>
                </div>

                {selectedCategory ? (
                  <div className="flex items-center gap-2" key={selectedCategory.id}>
                    <Badge className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedCategory.color }}></div>
                      {selectedCategory.name}
                    </Badge>
                    <button
                      type="button"
                      className="ml-1 rounded-full outline-none hover:bg-muted"
                      onClick={handleRemoveCategory}
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove</span>
                    </button>
                  </div>
                ) : (
                  <Select
                    value={eventData.categoryId || 'placeholder'}
                    onValueChange={(value) => {
                      if (value === 'placeholder') return;
                      setEventData({...eventData, categoryId: value});
                    }}
                  >
                    <SelectTrigger id="category">
                      {(() => {
                        const selected = categories.find(c => c.id === eventData.categoryId);
                        if (selected) {
                          return (
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selected.color }}></div>
                              {selected.name}
                            </div>
                          )
                        }
                        return t('agenda.selectCategory');
                      })()}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder" disabled>
                        {t('agenda.selectCategory')}
                      </SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                            {category.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              )}


              {!isAllDay && user.account_type === 'C' && invoiceType === "T" && (
              <div className="grid gap-2 bg-gray-50 rounded-md p-4 relative">
                <div className="flex items-center justify-between">
                  <Label htmlFor="category">{t('agenda.category')}</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddCategoryForm(true)}
                    className="h-8 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> {t('agenda.addNew')}
                  </Button>
                </div>

                {selectedCategory ? (
                  <div className="flex items-center gap-2" key={selectedCategory.id}>
                    <Badge className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedCategory.color }}></div>
                      {selectedCategory.name}
                    </Badge>
                    <button
                      type="button"
                      className="ml-1 rounded-full outline-none hover:bg-muted"
                      onClick={handleRemoveCategory}
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove</span>
                    </button>
                  </div>
                ) : (
                  <Select
                    value={eventData.categoryId !== -1 ? eventData.categoryId : ""}
                    onValueChange={(value) => setEventData({ ...eventData, categoryId: value })}
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder={t('agenda.selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-1">
                        {t('agenda.selectCategory')}
                      </SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                            {category.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

              </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="date">Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full flex justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {eventData.start ? new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { dateStyle: 'long' }).format(new Date(eventData.start)) : <span>{locale === 'fr' ? 'Choisir une date' : 'Pick a date'}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 pointer-events-auto">
                      <Calendar
                        mode="single"
                        selected={eventData.start}
                         onSelect={handleDateSelect}
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {!isAllDay && (
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                    <Label htmlFor="startTime">{t('agenda.startTime')}</Label>
                    <CustomTimePicker
                      value={eventData.startTime}
                      onChange={(newStartTime) => {
                        const [hours, minutes] = newStartTime.split(":").map(Number);
                        const newStartDateTime = new Date(eventData.start);
                        newStartDateTime.setHours(hours, minutes, 0, 0);

                        const newEndDateTime = new Date(newStartDateTime.getTime() + duration * 60000);
                        const newEndTime = format(newEndDateTime, "HH:mm");

                        handleTimeChange(newStartTime, newEndTime);
                      }}
                    />
                  </div>

                   <div className="grid gap-2">
                    <Label htmlFor="endTime">{t('agenda.endTime')}</Label>
                    <CustomTimePicker
                      value={eventData.endTime}
                      onChange={(newEndTime) => {
                        handleTimeChange(eventData.startTime, newEndTime);
                      }}
                    />
                  </div>
                </div>
                )}

                {isAllDay && (
                  <div className="grid gap-2">
                    <Label htmlFor="end-date">{t('agenda.endDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                      <Button
                      variant="outline"
                      className="w-full flex justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {eventData.end && eventData.start ? (
                          new Intl.DateTimeFormat(
                            locale === 'fr' ? 'fr-FR' : 'en-US',
                            { dateStyle: 'long' }
                          ).format(getInclusiveEndDateForDisplay(new Date(eventData.end), new Date(eventData.start)))
                        ) : (
                          <span>{locale === 'fr' ? 'Choisir une date' : 'Pick a date'}</span>
                        )}
                    </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={eventData.end && eventData.start
                          ? getInclusiveEndDate(new Date(eventData.end), new Date(eventData.start))
                          : undefined
                        }
                        onSelect={(date) => {
                          if (!date) return;
                          const newEnd = new Date(date);
                          newEnd.setHours(0, 0, 0, 0);

                          // stocke en exclusive
                          if (eventData.start && newEnd.getTime() > new Date(eventData.start).getTime()) {
                            newEnd.setDate(newEnd.getDate() + 1);
                          }

                          setEventData({...eventData, end: newEnd});
                        }}
                        className="pointer-events-auto"
                        disabled={(date) => {
                          if (!eventData.start) return false;
                          const startDate = new Date(eventData.start);
                          startDate.setHours(0, 0, 0, 0);
                          return date.getTime() < startDate.getTime();
                        }}
                      />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

              </div>

              {!isAllDay && (
              <div className="mt-2 grid gap-2">
                <Label htmlFor="duration"> {t('agenda.duration')} ({t('agenda.minutes')})</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  value={duration}
                  onChange={handleDurationChange}
                  className="w-full"
                />
              </div>
              )}

              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="location">{t('agenda.location')} <span className="text-xs text-muted-foreground">({t('agenda.optional')})</span></Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddLocationForm(true)}
                    className="h-8 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> {t('agenda.addNew')}
                  </Button>
                </div>

                {selectedLocation ? (
                  <div className="flex items-center gap-2">
                    <Badge className="flex items-center gap-2">
                      <span>{selectedLocation.name}</span>
                    </Badge>
                    <button
                      type="button"
                      className="ml-1 rounded-full outline-none hover:bg-muted"
                      onClick={handleRemoveLocation}
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove</span>
                    </button>
                  </div>
                ) : (
                  <Select
                    value={eventData.locationId}
                    onValueChange={(value) => setEventData({...eventData, locationId: value})}
                  >
                    <SelectTrigger id="location">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {event && event.extendedProps && event.extendedProps.type === 100 && (
                <>
                <div className="bg-gray-50 rounded-md p-4 mt-4">
                  <div className="mt-2 grid gap-2">
                      <Label htmlFor="price">{user.account_type === "S" ? t('categories.price_per_student') : t('agenda.createNew.price')}</Label>
                      <Input
                        id="price"
                        value={eventData.price}
                        placeholder={t('agenda.createNew.titlePlaceholder')}
                        type="number"
                        inputMode="numeric"
                        className="w-full"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        onChange={(e) => setEventData({...eventData, price: e.target.value})}
                      />
                    </div>
                    <div className="mt-4 grid gap-2">
                      <Label htmlFor="extra">{t('agenda.createNew.extra')}</Label>
                      <Input
                        id="extra"
                        value={eventData.extra}
                        placeholder="0"
                        type="number"
                        inputMode="numeric"
                        className="w-full"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        onChange={(e) => setEventData({...eventData, extra: e.target.value})}
                      />
                    </div>
                  </div>
                </>
              )}



              {event && user.account_type === "S" && event.extendedProps && event.extendedProps.type === 100 && invoiceType === "S" && (
                <>
                <div className="bg-gray-50 rounded-md p-4">
                  <div className="mt-2 grid gap-2">
                      <Label htmlFor="price">{t('categories.price_per_teacher')}</Label>
                      <Input
                        id="price_teacher"
                        value={eventData.price_teacher}
                        placeholder={t('agenda.createNew.titlePlaceholder')}
                        type="number"
                        inputMode="numeric"
                        className="w-full"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        onChange={(e) => setEventData({...eventData, price_teacher: e.target.value})}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="mt-2 grid gap-2">
                  <Label htmlFor="title">{t('agenda.createNew.title')}</Label>
                  <Input
                    id="title"
                    value={eventData.title}
                    placeholder={t('agenda.createNew.titlePlaceholder')}
                    type="text"
                    className="w-full"
                    onChange={(e) => setEventData({...eventData, title: e.target.value})}
                  />
                </div>

              <div className="grid gap-2">
                <Label htmlFor="notes">{t('agenda.notes')}</Label>
                <span className='text-xs'>
                  {isAllDay ? t('agenda.createNew.noteInfo') : ''}
                </span>
                <Textarea
                  id="note"
                  placeholder="Add a note for this lesson"
                  value={eventData.note}
                  onChange={(e) => setEventData({...eventData, note: e.target.value})}
                />
              </div>

              {selectedCategory && eventData.studentIds && eventData.studentIds.length > 0 && !isAllDay && (
                <div className="p-4 bg-gray-50 rounded-md border border-gray-200 mt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HandCoins className="h-5 w-5 text-muted-foreground" />
                      <span className="text-sm font-medium">Pricing</span>
                    </div>
                    <Badge variant="outline" className="font-semibold">
                      Total: ${calculateTotalPrice().toFixed(2)}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                    <div>{t('agenda.category')}:</div>
                    <div>{selectedCategory.name}</div>

                    <div>{t('agenda.paymentType')}:</div>
                    <div>{getPaymentTypeLabel(selectedCategory)}</div>

                    <div>{t('agenda.numberOfStudents')}:</div>
                    <div>{eventData.studentIds.length}</div>

                    <div>{t('agenda.duration')}:</div>
                    <div>{duration} {t('agenda.minutes')}</div>

                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {showDeleteConfirmation || isDropdownOpen ? null : (
        <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-gray-100 z-20 pb-8" : "pt-5"}>
          {!isEditing ? (
            <>
              {!isEventLocked && !isCompleted && (isOwned || ((user.account_type === 'C' && event.extendedProps.invoice_type === 'T' && isTeacherEvent) ||
                  (user.account_type === 'S' && event.extendedProps.invoice_type === 'S'))) && (
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={isDeleting}
                  className="mr-auto"
                >
                  <Trash2 />
                </Button>
              )}


              {user.account_type === 'C' && user.roleType === "teachers_all" && !isOwned && isTeacherEvent && event.extendedProps.type === 100 && !isCompleted && (
                <div className="flex items-center text-xs text-muted-foreground">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddExtraForm(true)}
                     className="mr-auto"
                  >
                    <Cog className="h-4 w-4" />
                    {t('agenda.addExtraExpenses')}
                  </Button>
                </div>
              )}
              

                {isCompleted && event && event.extendedProps ? (
                 ((user.account_type === 'C' && event.extendedProps.invoice_type === 'T') ||
                  (user.account_type === 'S' && event.extendedProps.invoice_type === 'S')) ? (
                    <Badge className="mr-auto bg-green-200 text-sm cursor-pointer" onClick={toggleDropdown}>
                      <Lock className="h-4 w-4 mr-1 text-dark" />
                      {t('agenda.completed')}
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Badge>
                  ) : (
                    <Badge className="mr-auto bg-green-200 text-sm">
                      <Lock className="h-4 w-4 mr-1 text-dark" />
                      {t('agenda.completed')}
                    </Badge>
                  )
                ) : null}





              {isEventPast &&
                !isCompleted &&
                !isEventLocked &&
                event &&
                event.extendedProps &&
                (
                  event.extendedProps.type === 10 ||
                  event.extendedProps.type === 100
                ) &&
                !(
                  user.account_type === 'S' &&
                  event.extendedProps.invoice_type !== 'S'
                ) &&
                !(
                  user.account_type === 'C' &&
                  (event.extendedProps.invoice_type === 'T' || event.extendedProps.invoice_type === 'S') &&
                  !isTeacherEvent
                )
                && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Button
                            variant="default"
                            onClick={handleMarkAsCompleted}
                            className="bg-green-400 hover:bg-green-500"
                            disabled={user.account_type === "C" && !selectedCategory && invoiceType === "T" && event && (
                              event.extendedProps.type === 10
                            )}
                          >
                            <Check className="h-4 w-4" />
                            {t('agenda.markEventAsCompleted')}
                          </Button>
                        </div>
                      </TooltipTrigger>

                        {user.account_type === "C" && !selectedCategory && invoiceType === "T" && event && (
                          event.extendedProps.type === 10
                        ) && (
                        <TooltipContent side="top" align="center">
                          <p>{t('agenda.selectCategoryFirst')}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}


              {!isCompleted && (isOwned || ((user.account_type === 'C' && event.extendedProps.invoice_type === 'T' && isTeacherEvent) ||
                  (user.account_type === 'S' && event.extendedProps.invoice_type === 'S'))) && (
                    <Button onClick={() => setIsEditing(true)} variant="outline">
                        <Edit className="h-4 w-4" />
                        {t('agenda.edit')}
                    </Button>
                )}

              {isCompleted && eventStudents && eventStudents.length > 0 &&
                !(
                  user.account_type === 'S' &&
                  event.extendedProps.invoice_type === 'T'
                ) &&
                !(
                  user.account_type === 'C' &&
                  event.extendedProps.invoice_type === 'S'
                ) && (
                <Popover
                  open={isInvoiceDropdownOpen && eventStudents?.length > 1}
                  onOpenChange={setIsInvoiceDropdownOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      disabled={
                        eventStudents?.length === 1 &&
                        event.extendedProps.invoicedEvent?.some(inv => inv.student_id == eventStudents[0].id && inv.event_id == event.id)
                      }
                      onClick={() => {
                        if (eventStudents?.length === 1) {
                          const isInvoiced = event.extendedProps.invoicedEvent?.some(inv => inv.student_id == eventStudents[0].id && inv.event_id == event.id);
                          if (!isInvoiced) {
                            navigateToInvoices(eventStudents[0].id);
                          }
                        }
                      }}
                    >
                      {eventStudents?.length === 1 ? (
                        event.extendedProps.invoicedEvent?.some(inv => inv.student_id == eventStudents[0].id && inv.event_id == event.id) ? (
                          <>
                            <FileText className="mr-2 h-4 w-4 text-green-500" />
                            <span>{t('agenda.alreadyInvoiced')}</span>
                          </>
                        ) : (
                          <>
                            <Receipt className="mr-2 h-4 w-4" />
                            <span>{t('agenda.createInvoice')}</span>
                          </>
                        )
                      ) : (
                        <>
                          {eventStudents?.every(student =>
                            event.extendedProps.invoicedEvent?.some(inv => inv.student_id == student.id && inv.event_id == event.id)
                          ) ? (
                            <>
                              <FileText className="mr-2 h-4 w-4 text-green-500" />
                              <span>{t('agenda.allInvoiced')}</span>
                            </>
                          ) : (
                            <>
                              <Receipt className="mr-2 h-4 w-4" />
                              <span>{t('agenda.createStudentInvoice')}</span>
                            </>
                          )}
                          <ChevronDown className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </PopoverTrigger>

                  {eventStudents?.length > 1 && (
                    <PopoverContent className="w-56">
                      {eventStudents?.map((student) => {
                        const isInvoiced = event.extendedProps.invoicedEvent?.some(
                          (inv: any) => inv.student_id == student.id && inv.event_id == event.id
                        );
                        return (
                          <div
                            key={student.id}
                            className={`flex items-center p-2 text-sm ${
                              isInvoiced ? "cursor-default opacity-60" : "cursor-pointer hover:bg-gray-100"
                            }`}
                            onClick={() => !isInvoiced && navigateToInvoices(student.id)}
                          >
                            <User className="mr-2 h-4 w-4" />
                            <span className="flex-1">{student.name}</span>
                            {isInvoiced && <FileText className="h-3 w-3 text-green-500 ml-2" />}
                          </div>
                        );
                      })}
                    </PopoverContent>
                  )}
                </Popover>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-col items-start">
                {eventData.is_recurrent && (
                  <div className="mt-2 mb-4">
                    <input
                      type="checkbox"
                      id="saveAllRecurrence"
                      onChange={(e) => setSaveAllRecurrence(e.target.checked)}
                      checked={saveAllRecurrence}
                    />
                    <label htmlFor="saveAllRecurrence" className="ml-2">
                      {t('agenda.saveAllRecurrence')} ?
                    </label>
                  </div>
                )}

              <div className="flex">
                <div className="relative flex">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      if (event) {
                        const startTime = format(new Date(event.start), 'HH:mm');
                        const endTime = format(new Date(event.end), 'HH:mm');
                        const eventStart = new Date(event.start);
                        const eventEnd = event.end ? new Date(event.end) : eventStart;
                        const teacherIds = (event.extendedProps.teachersIds?.length ?? 0) > 0
                        ? event.extendedProps.teachersIds
                        : [event.extendedProps.teacherId];
                        setEventData({
                          id: event.id,
                          title: event.title,
                          start: eventStart,
                          end: eventEnd,
                          startTime: startTime,
                          endTime: endTime,
                          studentIds: event.extendedProps?.studentIds || [],
                          teacherId: teacherIds,
                          categoryId: event.extendedProps?.categoryId,
                          locationId: event.extendedProps?.locationId || '',
                          completed: isCompleted,
                          note: event.extendedProps?.note || '',
                          is_recurrent: event.extendedProps?.is_recurrent || false,
                          original_event_id: event.extendedProps?.original_event_id || null,
                          price: event.extendedProps?.price || 0,
                          price_teacher: event.extendedProps?.price_teacher || 0,
                          extra: event.extendedProps?.extra || '',
                          costs_2: event.extendedProps?.costs_2 || 0,
                          costs_1_file: event.extendedProps?.costs_1_file || null,
                          costs_2_file: event.extendedProps?.costs_2_file || null,
                          isOwned: event.extendedProps?.isEventOwned || true,
                        });
                      }
                    }}
                  >
                    {t('common.cancel')}
                  </Button>

                  <Button onClick={() => handleSaveChanges(false)} disabled={isSubmitting} className="ml-2 rounded-r-none">
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        {t('common.processing')}
                      </span>
                    ) : (
                        t('common.save')
                    )}
                  </Button>
                  <div className="border-l border-gray-300"></div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="default"
                        className="rounded-l-none px-2"
                        disabled={isSubmitting}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => handleSaveChanges(true)} className='cursor-pointer'>
                        {t('agenda.saveAndContinue')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                </div>
              </div>
            </>
          )}
        </DialogFooter>
        )}

        {showDeleteConfirmation && (
          <div className={`bottom-0 left-0 right-0 p-3 pb-8 shadow-lg z-30 rounded-lg bg-red-50 shadow-lg ${isMobile ? 'absolute' : 'relative'}`}>
            <div className="flex items-center justify-between">
              <div className="flex-1 max-w-md">
                <h3 className="text-lg font-semibold">{t('agenda.confirmDeletion')}</h3>
                <p className="text-sm text-muted-foreground">
                   {!eventData.is_recurrent && (
                  <span className='font-semibold'>{t('agenda.confirmDelete')}</span>
                  )}
                  {eventData.is_recurrent && (
                    <div className="mt-2">
                      <input
                        type="checkbox"
                        id="deleteAllRecurrences"
                        checked={deleteAllRecurrences}
                        onChange={(e) => setDeleteAllRecurrences(e.target.checked)}
                      />
                      <label htmlFor="deleteAllRecurrences" className="ml-2">{t('agenda.deleteAllRecurrence')} ?</label>
                    </div>
                  )}
                </p>
              </div>
              <div className={`flex ${isMobile ? 'flex-col space-y-2 justify-end' : 'space-x-2 justify-end'}`}>
                <Button variant="outline" onClick={() => setShowDeleteConfirmation(false)} disabled={isDeleting}>
                  {t('common.cancel')}
                </Button>
                <Button variant="destructive" onClick={confirmDeleteEvent} disabled={isDeleting}>
                  {isDeleting ? (
                    <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
                  ) : (
                    t('common.delete')
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}


        {isDropdownOpen && (
          <div className={`bottom-0 left-0 right-0 p-3 pb-8 shadow-lg z-30 rounded-lg bg-green-50 shadow-lg ${isMobile ? 'absolute' : 'relative'}`}>
            <div className="flex items-center justify-between">
              <div className="flex-1 max-w-md">
                <h3 className="text-lg font-semibold">{t('agenda.confirmUnlock')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('agenda.unlock')}
                </p>
              </div>
              <div className={`flex ${isMobile ? 'flex-col space-y-2 justify-end' : 'space-x-2 justify-end'}`}>
                <Button variant="outline" onClick={() => toggleDropdown()} disabled={isProcessing}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleMarkAsUnCompleted} disabled={isProcessing}>
                  {isProcessing ? (
                    <Loader2 className="h-8 w-8 animate-spin text-white" />
                  ) : (
                    t('agenda.unlockButton')
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

      </>
    );
  };

  const confirmDeleteEvent = async () => {
    try {
      setIsDeleting(true);

      await eventService.deleteEvent(token, event.id, deleteAllRecurrences);

      toast({
        title: "Event deleted",
        description: "The event has been deleted successfully.",
      });

      onOpenChange(false);

      if (onRefreshEvents) {
        onRefreshEvents();
      }

      setEventData(null);
      setIsEditing(false);
      setIsInitialized(false);

    } catch (error) {
      console.error('Error deleting event:', error);

      toast({
        title: "Error deleting event",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  return (
  <Dialog open={open} onOpenChange={handleOpenChange}>
    <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
      <DialogDescription className="sr-only">
        {isEditing ? t('agenda.editLessonTitle') : t('agenda.scheduleLessonTitle')}
      </DialogDescription>
      {!isInitialized ? (
        <div className="flex justify-center items-center p-8 h-full overflow-hidden">
          <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
        </div>
      ) : (
        <>
          {/* Header (inchangé) */}
          {!showAddStudentForm && !showAddCategoryForm && !showAddLocationForm && !showStudentSelector && !showTeacherSelector ? (
            <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
              <DialogTitle>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <CalendarCog className="h-5 w-5 text-blue-500" />
                  ) : (
                    <CalendarIcon className="h-5 w-5 text-blue-500" />
                  )}
                  {event?.extendedProps?.type ? getEventTypeDescription(event.extendedProps.type) : ""}

                  {(user.account_type === 'S' || user.roleType === "teachers_all") && (event.extendedProps.type === 10 || event.extendedProps.type === 100) ? (event.extendedProps.invoice_type === 'T' ? ' - ' + t('agenda.invoiceTypeT') : ' - ' + t('agenda.invoiceTypeS')) : ''}
                </div>
              </DialogTitle>
                <DialogDescription className="sr-only">
                  {isEditing ? t('agenda.editLessonTitle') : t('agenda.scheduleLessonTitle')}
                </DialogDescription>
            </DialogHeader>
          ) : (
            <div className="flex items-center justify-between px-3 py-2 mb-2 m-2">
              <Button variant="outline" onClick={handleBackClick}>
                <ArrowLeft /> {t('common.back')}
              </Button>
            </div>
          )}

          {/* Contenu avec transition fluide */}
            <div
              className={`transition-opacity duration-500 ease-in-out flex flex-col h-full relative ${
                isContentVisible ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {renderMainContent()}
            </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
};

export default EventDetailModal;
